// Tableau de bord Geotab : lignes du tableau « Par chauffeur » (comparées à toute la flotte) et liste « À surveiller ».
import { fmtDelta, fmtDuration } from "./geotabFormat.js";

export const REGULAR_DAYS = 10; // moins de 10 journées : affiché sans comparaison, trop peu de jours
const pct = (done, total) => (total ? Math.round((100 * done) / total) : null);
const plural = (n, word) => `${n} ${word}${n > 1 ? "s" : ""}`;
const hoursOf = (h) => (h?.days ? { days: h.days, planned: h.planned / h.days, real: h.real / h.days, gap: (h.real - h.planned) / h.days, over: h.over } : null);
const kmGap = (km) => (km?.days && km.planned ? 100 * (km.real / km.planned - 1) : null);
// Comme le bandeau : arrondir les deux durées avant de calculer l'écart affiché.
const durationOf = (d) => {
  if (!d?.count) return null;
  const planned = Math.round(d.planned), real = Math.round(d.real);
  return { planned, real, gap: real - planned, pct: planned > 0 ? (100 * (real - planned)) / planned : null, count: d.count };
};
// Rapidité comparée aux collègues, en % : la route l'est déjà ; chargement et livraison ramenés au prévu de chaque visite.
const speedOf = (measure, metric) => {
  const pct = metric === "route" ? measure?.peer_gap : measure?.peer_gap_pct;
  return Number.isFinite(pct) ? { pct, minutes: metric === "route" ? null : measure.peer_gap, days: measure.days, count: measure.count } : null;
};
// Excès rapportés aux km roulés ; L/100 km en roulant, sans les litres brûlés à l'arrêt (ralenti, soufflerie, vis).
const per1000 = (d) => (d?.km > 0 ? (1000 * d.speeding) / d.km : null);
const rollingL100 = (f) => (f?.km > 0 && f.litres > 0 ? (100 * (f.litres - f.idle_site - f.idle_off)) / f.km : null);

// Une ligne : la barre du chauffeur (ou de la flotte) ramenée à des valeurs comparables.
function rowOf(name, days, bar, { km, speeding, idle, durations, performance, driving, trucks, regular = true }) {
  return {
    name, days, regular,
    loads: pct(bar.loads.done, bar.loads.planned), deliveries: pct(bar.deliveries.done, bar.deliveries.planned),
    loadCounts: [bar.loads.done, bar.loads.planned],
    loading: durationOf(durations?.loading), delivery: durationOf(durations?.delivery),
    order: pct(bar.order.ok, bar.order.count),
    pm: [bar.pm.done, bar.pm.planned], preload: [bar.preloads.days - bar.preloads.reloaded, bar.preloads.days], p1: [bar.p1.ok, bar.p1.count],
    off: bar.truck_days ? bar.off_minutes / bar.truck_days : null,
    hours: hoursOf(bar.hours), km: kmGap(km), speeding: per1000(driving), speedingPerDay: speeding, idle,
    l100: rollingL100(driving?.fuel), driving: driving || null, trucks: trucks || [],
    speed: performance ? Object.fromEntries(["route", "loading", "delivery"].map((m) => [m, speedOf(performance[m], m)])) : null,
  };
}

export function fleetRow(data) {
  const f = data.fleet;
  return rowOf("Toute la flotte", data.bar.truck_days, data.bar, {
    durations: data.bar.timing?.durations, driving: f.driving,
    km: f.km, speeding: f.truck_days ? f.speeding / f.truck_days : null, idle: f.truck_days ? f.idle_minutes / f.truck_days : null,
  });
}

export function driverRows(data) {
  return (data.drivers || []).map((d) => rowOf(d.name, d.days, d.bar, {
    durations: d.durations, performance: d.performance, driving: d.driving, trucks: d.trucks,
    km: d.km, speeding: d.speeding_per_day, idle: d.idle_per_day, regular: d.days >= REGULAR_DAYS,
  }));
}

// Comparé à la flotte : « worse » (rouge) ou « better » (vert) au-delà d'un écart qui compte, sinon rien.
const RULES = {
  loads: (v, f) => [v <= f - 5, v >= f + 5],
  deliveries: (v, f) => [v <= f - 5, v >= f + 5],
  order: (v, f) => [v <= f - 5, v >= f + 5],
  off: (v, f) => [v >= f + 15, v <= f - 15],
  km: (v, f) => [v >= f + 5, v <= f - 5],
  speeding: (v, f) => [v >= 1.5 * f, v <= 0.5 * f],
  idle: (v, f) => [v >= f + 20, v <= f - 20],
  l100: (v, f) => [v >= 1.1 * f, v <= 0.9 * f],
};

// Écart sur place, en minutes par visite : coloré à ±10 min, sur la minute affichée. Un % sur un arrêt court grossissait
// les petits écarts (+7 min sur 28 = +25 %). La route reste en %, colorée à 5 %.
export const GAP_MINUTES = 10;
export function gapCounts(minutes) {
  return minutes != null && Math.abs(Math.round(minutes)) >= GAP_MINUTES;
}

export function speedCounts(metric, speed) {
  return !!speed && (metric === "route" ? Math.abs(speed.pct) >= 5 : gapCounts(speed.minutes));
}

export function compareToFleet(key, value, fleetValue) {
  const rule = RULES[key];
  if (!rule || value == null || fleetValue == null) return null;
  const [worse, better] = rule(value, fleetValue);
  return worse ? "worse" : better ? "better" : null;
}

// Écart d'heures : couleurs des écarts de Geotab, à 30 min près.
export function hoursTone(gap) {
  if (gap == null) return "empty";
  if (Math.abs(gap) <= 30) return "ok";
  if (gap > 0) return gap <= 60 ? "warn" : "bad";
  return gap >= -60 ? "early" : "early2";
}

// Valeur de tri d'une colonne ; « none » en dernier, quel que soit le sens.
const SORT_VALUE = {
  name: (r) => r.name, days: (r) => r.days, loads: (r) => r.loads, deliveries: (r) => r.deliveries, order: (r) => r.order,
  pm: (r) => (r.pm[1] ? r.pm[0] / r.pm[1] : null), preload: (r) => (r.preload[1] ? r.preload[0] / r.preload[1] : null),
  p1: (r) => (r.p1[1] ? r.p1[0] / r.p1[1] : null), off: (r) => r.off, hoursPlanned: (r) => r.hours?.planned ?? null,
  loadingPlanned: (r) => r.loading?.planned ?? null, loadingReal: (r) => r.loading?.real ?? null, loadingGap: (r) => r.loading?.gap ?? null,
  deliveryPlanned: (r) => r.delivery?.planned ?? null, deliveryReal: (r) => r.delivery?.real ?? null, deliveryGap: (r) => r.delivery?.gap ?? null,
  speedRoute: (r) => r.speed?.route?.pct ?? null, speedLoading: (r) => r.speed?.loading?.minutes ?? null, speedDelivery: (r) => r.speed?.delivery?.minutes ?? null,
  hoursReal: (r) => r.hours?.real ?? null, hoursGap: (r) => r.hours?.gap ?? null, hoursOver: (r) => (r.hours ? r.hours.over / r.hours.days : null),
  km: (r) => r.km, speeding: (r) => r.speeding, idle: (r) => r.idle, l100: (r) => r.l100,
};

export function sortRows(rows, key, direction = "desc") {
  const value = SORT_VALUE[key] || SORT_VALUE.days;
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    return typeof x === "string" ? sign * x.localeCompare(y, "fr") : sign * (x - y);
  });
}

const dayMonth = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { day: "numeric", month: "short" });

// À surveiller : ce qui demande une action d'abord (warning), puis l'information, puis ce qui va bien.
export function watchItems(data) {
  const bar = data.bar;
  const f = data.fleet;
  const items = [];
  const missingLoads = bar.loads.planned - bar.loads.done + bar.pm.planned - bar.pm.done;
  const missingDeliveries = bar.deliveries.planned - bar.deliveries.done;
  const missing = missingLoads + missingDeliveries;
  if (missing) {
    items.push({ key: "missing", tone: "warning", title: `${plural(missing, "arrêt")} non fait${missing > 1 ? "s" : ""}`,
      detail: `${plural(missingLoads, "chargement")} · ${plural(missingDeliveries, "livraison")}`,
      days: (data.worst_days || []).map((d) => ({ date: d.date, label: `${dayMonth(d.date)} (${d.missing})` })) });
  }
  const p1 = bar.p1;
  if (p1.count && p1.ok < p1.count) {
    const plannedOut = (data.p1 || []).filter((x) => x.planned_out).length;
    items.push({ key: "p1", tone: "warning", title: `P1 : ${p1.ok} sur ${p1.count} dans la fenêtre`,
      detail: plannedOut ? `${plannedOut} déjà prévue${plannedOut > 1 ? "s" : ""} hors fenêtre par le Dispatch` : `${p1.early} en avance · ${p1.late} en retard · ${p1.missing} non faite${p1.missing > 1 ? "s" : ""}` });
  }
  const safety = Object.values(f.safety || {});
  if (safety.length && safety.every((n) => !n)) {
    items.push({ key: "safety", tone: "warning", title: "Règles de sécurité Geotab sans événement",
      detail: `Freinage brusque, accélération, virage, ceinture : 0 en ${data.days} jours. Vérifier qu'elles sont actives dans MyGeotab.` });
  }
  if (f.duty.long) {
    items.push({ key: "duty", tone: "warning", title: `${plural(f.duty.long, "journée")} de service de plus de 14 h`,
      detail: `Carnet de route Geotab · médiane ${fmtDuration(f.duty.median)}` });
  }
  if (f.identified.other) {
    const names = (data.drivers || []).filter((d) => d.other_sessions).sort((a, b) => b.other_sessions - a.other_sessions).slice(0, 3);
    items.push({ key: "identified", tone: "warning", title: `${plural(f.identified.other, "journée")} sous le nom d'un autre chauffeur`,
      detail: names.map((d) => `${d.name} ${d.other_sessions}`).join(" · "), view: "devices" });
  }
  const long = driverRows(data).filter((d) => d.regular && d.hours && d.hours.gap > 90).sort((a, b) => b.hours.gap - a.hours.gap);
  if (long.length) {
    items.push({ key: "drivers", tone: "warning", title: `${plural(long.length, "chauffeur")} à plus de 1 h 30 au-delà du prévu`,
      detail: `${long.map((d) => `${d.name} ${fmtDelta(d.hours.gap)}`).join(" · ")} par jour en moyenne`, drivers: true });
  }
  const short = (data.hour_bands || []).filter((b) => b.max != null && b.max <= 480 && b.days);
  const shortDays = short.reduce((n, b) => n + b.days, 0);
  const shortGap = shortDays ? short.reduce((n, b) => n + b.gap * b.days, 0) / shortDays : null;
  if (shortGap != null && shortGap > 60) {
    items.push({ key: "short", tone: "warning", title: `Journées prévues de moins de 8 h : ${fmtDelta(shortGap)} en moyenne`,
      detail: `${plural(shortDays, "journée")} complète${shortDays > 1 ? "s" : ""} : le travail donné dépasse les heures prévues` });
  }
  const pre = bar.preloads;
  if (pre.days) {
    items.push({ key: "preloads", tone: pre.reloaded ? "info" : "ok",
      title: `Préchargements : ${pre.days - pre.reloaded} départs chargés sur ${pre.days}`, detail: `${plural(pre.orders, "commande")} chargée${pre.orders > 1 ? "s" : ""} la veille` });
  }
  const rank = { warning: 0, info: 1, ok: 2 };
  return items.sort((a, b) => rank[a.tone] - rank[b.tone]);
}

export { pct, plural, dayMonth };
